import { useLayoutEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import LeadsFilterCard from "./LeadsFilterCard";
import LeadsFilterItems from "./LeadsFilterItems";
import LeadsStatusTabs from "./LeadsStatusTabs";
import LeadsTable from "./LeadsTable";
import LeadsSkeleton from "./LeadsSkeleton";
import LeadDetailDrawer from "./LeadDetailDrawer";
import ReportLeadsModal from "./ReportLeadsModal";
import RejectLeadsModal from "./RejectLeadsModal";
import BrandMatchModal from "./BrandMatchModal";

interface LeadsProps {
  data: LeadsManagementData;
}

export default function Leads({ data }: LeadsProps) {
  // Remember how tall the table was last time it rendered, so the loading
  // skeleton that replaces it (page/tab/filter change) keeps the same height
  // instead of collapsing to a few rows and making the page jump.
  const tableAreaRef = useRef<HTMLDivElement>(null);
  const [lastTableHeight, setLastTableHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!data.isLoading && tableAreaRef.current) {
      setLastTableHeight(tableAreaRef.current.offsetHeight);
    }
  }, [data.isLoading, data.pageLeads]);

  return (
    <Box>
      <LeadsFilterCard data={data} />
      <LeadsStatusTabs data={data} />
      <LeadsFilterItems data={data} />

      <Typography
        variant="body2"
        sx={{
          color: "info.main",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mt: 1,
          py: 0.5,
          fontSize: 16,
          lineHeight: 1.5,
          letterSpacing: "0.15px",
          fontWeight: 500,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 22, marginRight: 1.5 }} />
        Click on the full name to view lead information.
      </Typography>

      <Box
        ref={tableAreaRef}
        sx={{
          mt: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: data.isLoading && lastTableHeight ? lastTableHeight : undefined,
        }}
      >
        {data.isLoading ? <LeadsSkeleton height={lastTableHeight} /> : <LeadsTable data={data} />}
      </Box>

      <LeadDetailDrawer data={data} />
      <ReportLeadsModal data={data} />
      <RejectLeadsModal data={data} />
      <BrandMatchModal data={data} />
    </Box>
  );
}
